import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import type { Product } from '@/lib/types'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/primitives'
import { ProductFormFields, toDraft, useSaveProduct, type Draft } from '@/pages/ProductForm'
import { t } from '@/i18n'

/**
 * Edits a product from the inventory list.
 *
 * Only the chrome lives here — the fields and the saving are shared with the delivery screen, so
 * the two cannot come apart about what a product is. Adding a *new* product is not reached from
 * here any more: a product that has never been delivered has no real cost, so it is created as
 * part of the delivery that brought it in.
 */
export function ProductEditor({
  open,
  product,
  onClose,
  onSaved,
}: {
  open: boolean
  product: Product | null
  onClose: () => void
  /** Called with the product that was just created, when one was. */
  onSaved?: (created: { id: number; name: string }) => void
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(product, 11))
  /** Expiry dates the user has changed, by batch id. Written when the product is saved. */
  const [expiryEdits, setExpiryEdits] = useState<Record<number, string>>({})
  const { save, error, setError } = useSaveProduct(product)
  const queryClient = useQueryClient()

  // The shop's VAT setting drives both whether the field shows and what a new product starts at.
  const tax = useQuery({
    queryKey: ['settings', 'tax'],
    queryFn: () =>
      apiClient.get<{ vatEnabled: boolean; defaultVatRate: number }>('/api/settings/tax'),
    enabled: open,
  })

  useEffect(() => {
    if (open) {
      setDraft(toDraft(product, (tax.data?.defaultVatRate ?? 0.11) * 100))
      setExpiryEdits({})
      setError(null)
    }
  }, [open, product, tax.data?.defaultVatRate, setError])

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={product ? `Edit ${product.name}` : 'New product'}
      description={product ? `SKU ${product.sku}` : 'Add an item to the catalogue'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={() =>
              save.mutate(draft, {
                onSuccess: async (saved) => {
                  // Batch dates are a separate record from the product, so they are written
                  // separately — one call each, only for the ones actually changed. They go
                  // after the product save so a rejected product does not leave dates moved
                  // against a record that did not change.
                  const changed = Object.entries(expiryEdits)
                  if (changed.length > 0) {
                    await Promise.all(
                      changed.map(([batchId, date]) =>
                        apiClient.put(`/api/stock/batches/${batchId}/expiry`, {
                          expiryDate: date === '' ? null : date,
                        }),
                      ),
                    )
                    await queryClient.invalidateQueries({ queryKey: ['stock'] })
                  }

                  if (!product) onSaved?.(saved)
                  onClose()
                },
              })
            }
            disabled={save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save product'}
          </Button>
        </>
      }
    >
      <ProductFormFields
        draft={draft}
        onChange={setDraft}
        vatEnabled={tax.data?.vatEnabled !== false}
        productId={product?.id}
        expiryEdits={expiryEdits}
        onExpiryChange={(batchId, date) =>
          setExpiryEdits((current) => ({ ...current, [batchId]: date }))
        }
      />

      {error && (
        <p
          role="alert"
          className="mt-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500"
        >
          {error}
        </p>
      )}
    </Modal>
  )
}
